import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, openToast } from 'utils/localDispatcher';
import { ChatOrderAgreementModalStyle } from './ChatOrderAgreementModalStyle';
import { ChatOrderOpenIcon, ChevronRightIcon, CloseIcon } from 'utils/SVGIcons';
import IconCheckMotion from 'components/common/IconCheckMotion';
import { createStoreAndAgreement } from 'apis/CommerceGraphql/chatOrder';
import { setChatOrderEligibility } from 'store/ducks/chatOrderStore';
import { StoreState } from 'store/ducks';
import { DAANGN_BUSINESS_URL } from 'constants/config';
import { useSignItems } from 'hooks/useSignItems';

interface AgreementItem {
  id: string;
  label: string;
  required: boolean;
}

const AGREEMENT_ITEMS: AgreementItem[] = [
  { id: 'service', label: '채팅 주문 서비스 이용약관', required: true },
  { id: 'privacy-notice', label: '개인정보 수집 및 이용', required: true },
  { id: 'privacy-third-party', label: '제3자 정보 제공 동의', required: true },
];

interface ChatOrderAgreementModalProps {
  baId: string | number;
  onAgree?: () => void;
  onComplete?: (storeId: string) => void;
}

const ChatOrderAgreementModal = ({ baId, onAgree, onComplete }: ChatOrderAgreementModalProps) => {
  const dispatch = useDispatch();
  const eligibility = useSelector((state: StoreState) => state.chatOrderStore.chatOrderEligibility[String(baId)]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    values: termsState,
    toggleItem,
    sign,
    resetSign,
    isSigned,
    delays,
  } = useSignItems({
    initialValues: [false, false, false],
    checkAnimationMs: 400,
  });

  const handleItemClick = useCallback((_id: string) => {
    window.open(`${DAANGN_BUSINESS_URL}/terms/popup/chat-order/${_id}`, '_blank',
      `width=600,height=600`
    );
  }, []);

  const handleSubmit = () => {
    if (isSubmitting || isSigned) return;

    sign({
      onSigned: async () => {
        setIsSubmitting(true);
        try {
          const result = await createStoreAndAgreement({
            baId: String(baId),
            agreedToChatOrderTermsOfServiceAt: new Date().toISOString(),
          });

          if (result.error || !result.store?._id) {
            openToast('약관 동의에 실패했어요. 다시 시도해주세요.');
            setIsSubmitting(false);
            resetSign();
            return;
          }

          dispatch(
            setChatOrderEligibility({
              baId: String(baId),
              eligibility: {
                ...eligibility,
                store: {
                  ...eligibility?.store,
                  _id: result.store._id,
                  chatOrderProducts: eligibility?.store?.chatOrderProducts ?? [],
                },
                baById: result.store.businessAccount ?? eligibility?.baById,
              },
            })
          );
          onAgree?.();
          closeModal();
          onComplete?.(result.store._id);
        } catch {
          openToast('약관 동의에 실패했어요. 다시 시도해주세요.');
          setIsSubmitting(false);
          resetSign();
        }
      },
    });
  };

  return (
    <ChatOrderAgreementModalStyle>
      <button type="button" className="close-button" onClick={closeModal}>
        <CloseIcon size={24} />
      </button>

      <div className="icon-wrapper">
        <ChatOrderOpenIcon />
      </div>

      <div className="header-label">채팅 주문 오픈</div>
      <div className="title">채팅 안에서 주문부터 결제까지 간편해요</div>
      <div className="description">
        상품명과 가격을 입력한 주문서 하나로
        <br />
        거래와 정산이 쉬워져요.
      </div>

      <div className="agreement-section">
        <div className="agreement-title">채팅 주문을 위해 동의가 필요해요</div>
        <ul className="agreement-list">
          {AGREEMENT_ITEMS.map((item, index) => (
            <li key={item.id} className="agreement-item">
              <button
                type="button"
                className={`check-button ${termsState[index] || isSigned ? 'checked' : ''}`}
                onClick={() => toggleItem(index)}
                disabled={isSigned}
              >
                <span className="check-icon">
                  <IconCheckMotion
                    isChecked={termsState[index]}
                    animate={isSigned}
                    animateDelay={delays[index]}
                  />
                </span>
                <span className="item-label">
                  {item.required && <span>(필수)</span>} {item.label}
                </span>
              </button>
              <button type="button" className="arrow-button" onClick={() => handleItemClick(item.id)}>
                <ChevronRightIcon />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <button type="button" className="submit-button" onClick={handleSubmit} disabled={isSubmitting || isSigned}>
        {isSubmitting || isSigned ? '처리 중...' : '동의하고 시작하기'}
      </button>
    </ChatOrderAgreementModalStyle>
  );
};

export default ChatOrderAgreementModal;
